import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { Card } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MapRoundedIcon from '@mui/icons-material/MapRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import AddToCalendar from './AddToCalendar';
import { useI18n } from '../../i18n';
import './AddToCalendarPage.css';

/**
 * Public landing page for the "add to calendar" link that the booking-confirmation SMS points to
 * (host/add-to-calendar?t=..&s=..&d=..&l=..). Works WITHOUT login — the SMS recipient is usually
 * not authenticated. Reads the non-PII event fields from the query string and renders the shared
 * AddToCalendar block (Google + .ics) plus — when an address is present — the same Waze +
 * Google-Maps navigation used on the appointment-details ("הנחיות") screen.
 *
 *   t = title (department / service)
 *   s = start, local ISO "YYYY-MM-DDTHH:mm"
 *   d = duration in minutes
 *   l = location / address
 */
export default function AddToCalendarPage() {
  const { t, dir } = useI18n();
  const [params] = useSearchParams();
  const title = params.get('t') || t('addToCalendar.defaultEventTitle');
  const startStr = params.get('s') || '';
  const duration = parseInt(params.get('d'), 10) || 15;
  const location = params.get('l') || '';   // address — drives the Waze / Google-Maps nav below
  const details = params.get('n') || '';   // instructions + provider contact (event body)

  // embedded Google map overlay — opened by the "מפה" button, mirrors Appointment.js
  const [mapOpen, setMapOpen] = useState(false);

  const start = startStr ? dayjs(startStr) : null;
  const valid = start && start.isValid();

  const address = location;
  const mapsUrl = (mode) =>
    `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=${mode}`;
  // keyless embeddable map for the address (Hebrew UI). Loaded only when the user opens it.
  const mapEmbedUrl = `https://www.google.com/maps?q=${encodeURIComponent(address)}&z=16&hl=iw&output=embed`;
  const wazeUrl = `https://waze.com/ul?q=${encodeURIComponent(address)}&navigate=yes`;
  // Open the Waze APP first (waze:// scheme). If it isn't installed nothing takes over,
  // so after a short delay fall back to the web link. If the app opens, the page is
  // backgrounded → visibilitychange cancels the fallback.
  const openWaze = (e) => {
    if (e) e.preventDefault();
    if (!address) return;
    const q = encodeURIComponent(address);
    const appUrl = `waze://?q=${q}&navigate=yes`;
    const webUrl = `https://waze.com/ul?q=${q}&navigate=yes`;
    // fall back to the web link in a NEW tab so this page is preserved.
    const fallback = setTimeout(() => { window.open(webUrl, '_blank', 'noopener'); }, 1400);
    const cancel = () => { if (document.hidden) clearTimeout(fallback); };
    document.addEventListener('visibilitychange', cancel, { once: true });
    window.location.href = appUrl;
  };

  return (
    <div className="content_page" dir={dir}>
      <div className="content" style={{ padding: '24px 16px', textAlign: 'center' }}>
        <h1 className="sr-only">{t('addToCalendar.pageSrTitle')}</h1>
        <h3 style={{ color: '#1b3e72' }}>{t('addToCalendar.pageTitle')}</h3>
        {valid ? (
          <>
            <Card style={{ maxWidth: 340, margin: '12px auto', padding: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
              <div>{start.format('DD/MM/YYYY')} {t('common.atTime', { time: start.format('HH:mm') })}</div>
              {location ? <div style={{ marginTop: 6 }}><b>{location}</b></div> : null}
            </Card>
            <AddToCalendar
              title={title}
              start={start.toDate()}
              durationMinutes={duration}
              location={location}
              details={details}
            />

            {/* Navigation — Waze + Google-Maps, gated on the address being present.
                Same class names as the appointment-details screen → identical look. */}
            {address && (
              <Card className="atc-nav-card">
                <div className="atc-nav-title">{t('nav.navigation')}</div>
                <div className="provider-contact-wrap">
                  <div className="provider-contact-info">
                    <div className="provider-contact-row">{t('common.address')}: {address}</div>
                  </div>
                  {/* Waze — big icon. NO target="_blank": a same-context tap lets the phone
                      intercept the universal link and open the Waze app. */}
                  <a className="provider-waze-big" href={wazeUrl} onClick={openWaze} title={t('nav.wazeTitle')} aria-label={t('nav.wazeTitle')}>
                    <img src="/waze.svg" alt="Waze" className="provider-waze-img" />
                    <span className="provider-waze-label">Waze</span>
                  </a>
                </div>
                <div className="provider-maps">
                  <div className="maps-nav-title">{t('nav.mapsTitle')}</div>
                  <div className="maps-nav-links">
                    <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('driving')}>
                      <DirectionsCarIcon fontSize="small" /> {t('nav.car')}
                    </a>
                    <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('transit')}>
                      <DirectionsBusIcon fontSize="small" /> {t('nav.transit')}
                    </a>
                    <a className="maps-nav-link" target="_blank" rel="noopener noreferrer" href={mapsUrl('walking')}>
                      <DirectionsWalkIcon fontSize="small" /> {t('nav.walk')}
                    </a>
                    {/* "מפה" opens the map in an overlay dialog that rises above the page. */}
                    <button type="button" className="maps-nav-link maps-nav-mapbtn" onClick={() => setMapOpen(true)}>
                      <MapRoundedIcon fontSize="small" /> {t('nav.map')}
                    </button>
                  </div>
                </div>
              </Card>
            )}
          </>
        ) : (
          <p style={{ color: '#8a97a8' }}>{t('addToCalendar.invalidLink')}</p>
        )}

        {/* embedded Google map in an overlay dialog (opens above the page) */}
        <Dialog
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          fullWidth
          maxWidth="sm"
          className="map_dialog"
          PaperProps={{ className: 'map-dialog-paper' }}
        >
          <div className="map-dialog-head">
            <span>{address}</span>
            <button type="button" className="map-dialog-close" aria-label={t('common.close')} onClick={() => setMapOpen(false)}>
              <CloseRoundedIcon fontSize="small" />
            </button>
          </div>
          {mapOpen && (
            <iframe
              title={t('nav.map')}
              className="map-dialog-frame"
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          )}
        </Dialog>
      </div>
    </div>
  );
}
